/**
 * PhotoUploader — one row per photo requirement category.
 *
 * Behavior:
 *   - If no photo for this category yet: shows a labeled file picker. On
 *     mobile the camera capture is preferred via `capture="environment"`.
 *   - On upload success, automatically fires verify_photos for the new
 *     photo id. The result is rendered inline ("ok" / "retake" / "unclear").
 *   - Existing uploads for the same category are listed underneath with
 *     their last verdict (if known).
 */

import { useState, useRef, type ReactNode, type ChangeEvent } from 'react'
import { useTranslation }                  from 'react-i18next'
import { usePhotoUpload }                  from '@/hooks/usePhotoUpload'
import { useVerifyPhotos, type PhotoVerdict } from '@/hooks/useVerifyPhotos'
import { type TaskPhotoRow }               from '@/hooks/useTaskPhotos'
import { processImage }                    from '@/lib/photo/processImage'

interface Props {
  taskId:        string
  roomId:        string
  category:      string
  isRequired:    boolean
  existingPhotos: readonly TaskPhotoRow[]
}

export function PhotoUploader({ taskId, roomId, category, isRequired, existingPhotos }: Props): ReactNode {
  const { t }           = useTranslation()
  const inputRef        = useRef<HTMLInputElement>(null)
  const upload          = usePhotoUpload({ taskId })
  const verify          = useVerifyPhotos()
  const [verdict, setVerdict] = useState<PhotoVerdict | null>(null)
  const [preparing, setPreparing] = useState(false)
  const [prepareError, setPrepareError] = useState<string | null>(null)

  const matching = existingPhotos.filter((p) => p.photo_category === category)
  const hasPhoto = matching.length > 0

  const onPick = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const raw = e.target.files?.[0]
    if (!raw) return
    setVerdict(null)
    setPrepareError(null)

    // Normalize HEIC/HEIF, rotate by EXIF, resize, encode JPEG.
    let prepared: File
    try {
      setPreparing(true)
      prepared = await processImage(raw)
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      setPrepareError(t('photo.couldNotProcess', { message: msg }))
      return
    } finally {
      setPreparing(false)
      if (inputRef.current) inputRef.current.value = ''
    }

    // Upload errors are surfaced through `upload.error` (rendered below);
    // do not re-attribute them as prepare errors.
    upload.mutate({ file: prepared, category, roomId }, {
      onSuccess: (res) => {
        verify.mutate({ taskId, photoIds: [res.photoId] }, {
          onSuccess: (verdicts) => { setVerdict(verdicts[0] ?? null) },
        })
      },
    })
  }

  return (
    <li
      style={{
        padding:        '0.6rem 0',
        borderBottom:   '1px solid var(--brand-neutral-100)',
        display:        'flex',
        flexDirection:  'column',
        gap:            '0.4rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
        <div>
          <strong style={{ textTransform: 'capitalize' }}>{category.replace(/_/g, ' ')}</strong>
          {isRequired && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: 'var(--brand-danger)', fontWeight: 600 }}>{t('photo.required')}</span>}
          {hasPhoto && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--brand-accent)' }}>
              {t('photo.uploaded', { count: matching.length })}
            </span>
          )}
        </div>

        <label
          style={{
            display:      'inline-block',
            padding:      '0.4rem 0.8rem',
            background:   upload.isPending ? 'var(--brand-neutral-300)' : 'var(--brand-primary)',
            color:        'var(--brand-white)',
            borderRadius: 'var(--brand-radius-md)',
            fontSize:     '0.85rem',
            fontWeight:   600,
            cursor:       upload.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {preparing ? t('photo.preparing') : upload.isPending ? t('photo.uploading') : (hasPhoto ? t('photo.addAnother') : t('photo.addPhoto'))}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            capture="environment"
            disabled={upload.isPending || preparing}
            onChange={onPick}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      {prepareError && (
        <ErrorLine message={prepareError} />
      )}

      {upload.error && (
        <ErrorLine message={upload.error.userMessage} />
      )}

      {verify.isPending && (
        <Subtle>{t('photo.runningVisionCheck')}</Subtle>
      )}

      {verdict && (
        <VerdictLine verdict={verdict} />
      )}
    </li>
  )
}

function VerdictLine({ verdict }: { verdict: PhotoVerdict }): ReactNode {
  const { t } = useTranslation()
  // The 'unavailable' branch is the visible end of CLAUDE.md ERROR 16:
  // upstream AI outage produces a distinct, blocking-coloured affordance
  // that tells the cleaner to escalate. Never auto-pass on outage.
  const palette =
    verdict.verdict === 'ok'          ? { bg: 'var(--brand-accent-subtle)',  fg: 'var(--brand-accent)'  } :
    verdict.verdict === 'send_back'   ? { bg: 'var(--brand-danger-subtle)',  fg: 'var(--brand-danger)'  } :
    verdict.verdict === 'unavailable' ? { bg: 'var(--brand-danger-subtle)',  fg: 'var(--brand-danger)'  } :
                                        { bg: 'var(--brand-warning-subtle)', fg: 'var(--brand-warning)' }
  const label =
    verdict.verdict === 'ok'          ? t('photo.verdictOk') :
    verdict.verdict === 'send_back'   ? t('photo.verdictRetake') :
    verdict.verdict === 'unclear'     ? t('photo.verdictUnclear') :
    verdict.verdict === 'unavailable' ? t('photo.verdictUnavailable') :
                                        t('photo.verdictSkipped')

  return (
    <div
      role="status"
      style={{
        background:   palette.bg,
        color:        palette.fg,
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.4rem 0.6rem',
        fontSize:     '0.8rem',
      }}
    >
      <strong>{label}</strong>{verdict.reason && <> — {verdict.reason}</>}
    </div>
  )
}

function ErrorLine({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.4rem 0.6rem',
        fontSize:     '0.8rem',
      }}
    >
      {message}
    </div>
  )
}

function Subtle({ children }: { children: ReactNode }): ReactNode {
  return <div style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>{children}</div>
}
